import { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import CameraCapture from '../components/CameraCapture';

const COND_OPTIONS = [
  { value: 'no_damage',     label: '✅ No damage — returned clean' },
  { value: 'small_scratch', label: '⚠️ Small scratch / scuff' },
  { value: 'damage',        label: '🔴 Visible damage' },
  { value: 'major_damage',  label: '🆘 Major damage / accident' },
];

export default function EndRentalModal() {
  const { modals, closeModal, openModal, rentals, vehicles, customers, endRental, showToast, currentUserId, getDamagesForRental, getDamagesForVehicle, invoices, updateInvoice } = useApp();
  const open     = modals['end-rental'];
  const targetVid = modals['end-rental-vid'];

  const rental  = targetVid ? rentals.find(r => r.vid === targetVid && ['active','overdue'].includes(r.status)) : null;
  const vehicle = targetVid ? vehicles.find(v => v.id === targetVid) : null;

  const [cond, setCond]     = useState('no_damage');
  const [condNote, setCondNote] = useState('');
  const [odomKm, setOdomKm] = useState('');
  const [extraFee, setExtraFee] = useState('');
  const [extraNote, setExtraNote] = useState('');
  const [depositRefund, setDepositRefund] = useState('full');
  const [depositPartialAmt, setDepositPartialAmt] = useState('');
  const [collectMethod, setCollectMethod] = useState('cash');

  // Unpaid pro formas linked to this rental (e.g. an extension set to "collect at return")
  const pendingPF = rental ? (invoices || []).filter(i =>
    (i.contract === rental.id || i.contractId === rental.id) && i.status === 'pending'
  ) : [];
  const outstandingTotal = pendingPF.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const PAY_METHOD_LABEL = { cash: 'Cash', stripe: 'Stripe · Card', viva: 'Viva Wallet', bank: 'Bank Transfer', paypal: 'PayPal' };
  const [returnPhotos, setReturnPhotos]   = useState({});
  const [activeSlot, setActiveSlot]       = useState(null);
  const [camera, setCamera]               = useState(null);
  const returnPhotoRef                    = useRef(null);

  const PHOTO_SLOTS = ['Front', 'Rear', 'Left side', 'Right side', 'Dashboard', 'Interior'];

  function openSlotFile(label) {
    setActiveSlot(label);
    returnPhotoRef.current?.click();
  }
  function onSlotFile(e) {
    const file = e.target.files?.[0];
    if (!file || !activeSlot) return;
    const reader = new FileReader();
    reader.onload = ev => setReturnPhotos(prev => ({ ...prev, [activeSlot]: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openSlotCamera(label) {
    setCamera({ label: `${label} — Return`, setPhoto: (data) => setReturnPhotos(prev => ({ ...prev, [label]: data })) });
  }
  function removeSlotPhoto(label) {
    setReturnPhotos(prev => { const n = { ...prev }; delete n[label]; return n; });
  }

  function submit() {
    if (!rental) return;
    // Settle any outstanding pro formas (e.g. "collect at return" extension)
    if (outstandingTotal > 0 && updateInvoice) {
      pendingPF.forEach(pf => updateInvoice(pf.id, {
        status: 'paid',
        method: collectMethod,
        methodLabel: (PAY_METHOD_LABEL[collectMethod] || collectMethod) + ' (at return)',
      }));
    }
    endRental(targetVid, {
      extensionCollectedAtReturn: outstandingTotal > 0 ? outstandingTotal : 0,
      condRet:     cond,
      condRetNote: condNote,
      returnKm:    odomKm ? parseInt(odomKm) : null,
      extraFee:    extraFee ? parseFloat(extraFee) : 0,
      extraNote,
      depositRefund,
      depositReturned: depositRefund === 'full'
        ? (rental.depositCollected || rental.deposit || 0)
        : depositRefund === 'partial'
          ? parseFloat(depositPartialAmt || 0)
          : 0,
      depositDeducted: depositRefund === 'none'
        ? (rental.depositCollected || rental.deposit || 0)
        : depositRefund === 'partial'
          ? Math.max(0, (rental.depositCollected || rental.deposit || 0) - parseFloat(depositPartialAmt || 0))
          : 0,
      returnedAt: new Date().toISOString(),
      returnPhotos: PHOTO_SLOTS.filter(l => returnPhotos[l]).map(l => returnPhotos[l]),
      // Assignment: record who did check-out
      checkedOutBy: currentUserId,
      checkedOutAt: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      assignedTo: null,
      assignPhase: null,
    });
    closeModal('end-rental');
    showToast(`✓ Rental ended — ${vehicle?.name} is now available`);
    setCond('no_damage'); setCondNote(''); setOdomKm(''); setExtraFee(''); setExtraNote('');
    setDepositRefund('full'); setDepositPartialAmt('');
    setReturnPhotos({}); setCamera(null); setCollectMethod('cash');
  }

  if (!open) return null;

  return (
    <Modal open={!!open} onClose={() => closeModal('end-rental')}>
      <ModalHeader
        title="End Rental"
        subtitle={rental ? `${rental.id} · ${rental.customer}` : undefined}
        onClose={() => closeModal('end-rental')}
      />
      <ModalBody>
        {!rental ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--tx3)' }}>No active rental found for this vehicle.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Vehicle & rental summary */}
            <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Vehicle</span>
                <span style={{ fontWeight: 600 }}>{vehicle?.icon} {vehicle?.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Customer</span>
                <span>{rental.customer}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Rental period</span>
                <span>{rental.startLabel} → {rental.endLabel}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--tx3)' }}>Agreed price</span>
                <span style={{ fontWeight: 700, color: 'var(--gr)' }}>€{rental.agreedPrice}</span>
              </div>
            </div>

            {/* Outstanding pro formas to collect now (e.g. extension set to "collect at return") */}
            {outstandingTotal > 0 && (
              <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--am)', marginBottom: 6 }}>
                  💰 Due at return — collect €{outstandingTotal.toFixed(2)}
                </div>
                {pendingPF.map(pf => (
                  <div key={pf.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--tx2)', marginBottom: 2 }}>
                    <span>{pf.lines?.[0]?.desc || pf.notes || 'Pro forma'}</span>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(parseFloat(pf.total) || 0).toFixed(2)}</span>
                  </div>
                ))}
                <label className="fl" style={{ marginTop: 6 }}>Collect via</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, marginTop: 4 }}>
                  {[['cash', '💵 Cash'], ['stripe', '💳 Card'], ['viva', '🔵 Viva'], ['bank', '🏦 Bank']].map(([id, label]) => (
                    <div key={id} onClick={() => setCollectMethod(id)} style={{
                      padding: '6px 8px', border: `1.5px solid ${collectMethod === id ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 10.5, textAlign: 'center',
                      background: collectMethod === id ? 'var(--ac-l)' : 'var(--sur)',
                    }}>{label}</div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Pre-existing damages on this vehicle ─────────────── */}
            {vehicle && getDamagesForVehicle && (() => {
              const dmgs = getDamagesForVehicle(vehicle.id) || [];
              // Pre-existing = not yet repaired AND not from this rental (those would be new)
              const preExisting = dmgs.filter(d => !d.repaired && d.status !== 'resolved' && d.rentalId !== rental?.id);
              if (preExisting.length === 0) return null;
              return (
                <div style={{
                  background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
                  padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                    ⚠️ Pre-existing damages ({preExisting.length}) — already on this vehicle before this rental
                  </div>
                  <div style={{ fontSize: 10, color: '#92400e', marginBottom: 8 }}>
                    Don't charge the customer for these on return.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {preExisting.map(d => {
                      const sevColor = d.severity === 'major' ? '#ef4444'
                                    : d.severity === 'moderate' ? '#f59e0b'
                                    : '#10b981';
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => openModal('damage', d.id)}
                          style={{
                            background: '#fff', border: '1px solid #fde68a', borderRadius: 6,
                            padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8,
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sevColor, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {d.location || 'Damage'} — {d.description?.slice(0, 60)}{d.description?.length > 60 ? '…' : ''}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Return condition */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Return Condition</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {COND_OPTIONS.map(opt => (
                  <div key={opt.value} onClick={() => setCond(opt.value)} style={{
                    padding: '8px 12px', border: `1.5px solid ${cond === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                    background: cond === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                  }}>{opt.label}</div>
                ))}
              </div>
            </div>

            <div className="fg">
              <label className="fl">Condition notes</label>
              <textarea className="fi" value={condNote} onChange={e => setCondNote(e.target.value)} rows={2} placeholder="Describe any damage or notes..." />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="fg">
                <label className="fl">Odometer return (km)</label>
                <input type="number" className="fi" value={odomKm} onChange={e => setOdomKm(e.target.value)} placeholder="e.g. 125000" />
              </div>
              <div className="fg">
                <label className="fl">Extra charge (€)</label>
                <input type="number" className="fi" value={extraFee} onChange={e => setExtraFee(e.target.value)} placeholder="0" />
              </div>
              <div className="fg" style={{ gridColumn: '1/-1' }}>
                <label className="fl">Extra charge reason</label>
                <input type="text" className="fi" value={extraNote} onChange={e => setExtraNote(e.target.value)} placeholder="e.g. Fuel, cleaning fee, damage..." />
              </div>
            </div>

            {(() => {
              // Find any existing damage records for this rental
              const existingDamages = rental && getDamagesForRental ? getDamagesForRental(rental.id) : [];
              const showDamageBlock = (cond === 'small_scratch' || cond === 'damage' || cond === 'major_damage') || existingDamages.length > 0;
              if (!showDamageBlock) return null;
              // Pre-set severity based on what the operator selected
              const presetSeverity = cond === 'major_damage' ? 'major'
                                   : cond === 'damage'       ? 'moderate'
                                   : 'minor';
              const headlineText = cond === 'small_scratch'
                ? <>🔸 <strong>Small scratch / scuff</strong> — log it for the record (even if you waive the charge).</>
                : cond === 'major_damage'
                  ? <>🆘 <strong>Major damage / accident</strong> — log details, photos and consider insurance claim.</>
                  : <>⚠️ <strong>Damage reported</strong> — log it as a structured record so you can charge / dispute / claim later.</>;
              return (
                <div style={{ background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--ac)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div>
                      {existingDamages.length > 0
                        ? <>📋 <strong>{existingDamages.length} damage record{existingDamages.length !== 1 ? 's' : ''}</strong> linked to this rental</>
                        : headlineText
                      }
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        if (!rental) return;
                        const cust = customers.find(c => `${c.fname} ${c.lname}`.trim() === (rental.customer || '').trim());
                        openModal('damage', {
                          vid: rental.vid,
                          rentalId: rental.id,
                          customerId: cust?.id || null,
                          customerName: rental.customer || '',
                          description: condNote || '',
                          severity: presetSeverity,
                          // For small scratches, default to absorbed/waived (most common)
                          paidBy: cond === 'small_scratch' ? 'absorbed' : 'unpaid',
                          status: cond === 'small_scratch' ? 'dismissed' : 'reported',
                        });
                      }}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      + Damage Report
                    </button>
                  </div>
                  {existingDamages.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {existingDamages.map(d => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => openModal('damage', d.id)}
                          style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}
                        >
                          <span>{d.location || 'Damage'} — {d.description?.slice(0, 50)}{d.description?.length > 50 ? '…' : ''}</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(d.finalCost ?? d.estimatedCost ?? 0).toFixed(0)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Return photos */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
                Return Photos
                <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>
                  {Object.values(returnPhotos).filter(Boolean).length}/{PHOTO_SLOTS.length} added
                </span>
              </div>
              <input ref={returnPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onSlotFile} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                {PHOTO_SLOTS.map(label => (
                  <div key={label} style={{
                    border: `2px ${returnPhotos[label] ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                    borderRadius: 'var(--r)', overflow: 'hidden',
                    background: returnPhotos[label] ? 'transparent' : 'var(--sur2)',
                  }}>
                    {returnPhotos[label] ? (
                      <>
                        <img
                          src={returnPhotos[label]}
                          alt={label}
                          style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => window.open(returnPhotos[label], '_blank')}
                        />
                        <div style={{ padding: '4px 4px 3px', display: 'flex', gap: 3, justifyContent: 'center' }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotFile(label)}>📁</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotCamera(label)}>📷</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9, color: 'var(--ac)' }} onClick={() => removeSlotPhoto(label)}>✕</button>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: 9, color: 'var(--gr)', paddingBottom: 4, fontWeight: 600 }}>✓ {label}</div>
                      </>
                    ) : (
                      <div
                        style={{ padding: '16px 8px', textAlign: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}
                        onClick={() => openSlotFile(label)}
                      >
                        <div style={{ fontSize: 20, marginBottom: 4 }}>📷</div>
                        <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 9 }}>+ Add photo</div>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ marginTop: 6, fontSize: 9, padding: '2px 8px' }}
                          onClick={e => { e.stopPropagation(); openSlotCamera(label); }}
                        >
                          📸 Camera
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Deposit refund */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
                Deposit Refund {(rental.depositCollected || rental.deposit) ? <span style={{ color: 'var(--tx3)', fontWeight: 400 }}>— held: €{rental.depositCollected || rental.deposit}</span> : <span style={{ color: 'var(--gr)', fontWeight: 400 }}>— no deposit held</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {[
                  ['full',    '✅ Full refund'],
                  ['partial', '🔸 Partial refund'],
                  ['none',    '❌ No refund — damage / fees'],
                ].map(([val, label]) => (
                  <div key={val} onClick={() => setDepositRefund(val)} style={{
                    padding: '8px 12px', border: `1.5px solid ${depositRefund === val ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                    background: depositRefund === val ? 'var(--ac-l)' : 'var(--sur)',
                  }}>{label}</div>
                ))}
              </div>
              {depositRefund === 'partial' && (
                <div className="fg" style={{ marginTop: 8 }}>
                  <label className="fl">Refund amount (€)</label>
                  <input type="number" className="fi" value={depositPartialAmt} onChange={e => setDepositPartialAmt(e.target.value)} placeholder={`Max €${rental.depositCollected || rental.deposit || 0}`} />
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('end-rental')}>Cancel</button>
        <button className="btn btn-primary" style={{ background: 'var(--ac)' }} onClick={submit} disabled={!rental}>
          ✓ End Rental
        </button>
      </ModalFooter>
      {camera && (
        <CameraCapture
          label={camera.label}
          onCapture={data => { camera.setPhoto(data); setCamera(null); }}
          onClose={() => setCamera(null)}
        />
      )}
    </Modal>
  );
}
